import { Box, Button, FormControl, Stack, TextField, Typography } from '@mui/material'
import {
  CarrierVisitDirection,
  ContainerDamageResponseDto,
  GateDirection,
  OrderResponseDto,
  TruckVisitWithOrdersDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { ContainerDamageRecordStep } from '@planning/components'
import { GateDirectionIcon } from '@planning/pages/GateControl/Atoms/GateDirectionIcon'
import { useNavigationStore } from '@planning/stores'
import { GateInViewStore } from '@planning/stores/gateClerk/GateInViewStore'
import { NonNumericOrderWithPickUpAmount } from '@planning/stores/truckAppointment/TruckAppointmentDetailsViewStore'
import { canOrderProceed } from '@planning/utils/order-utils'
import { getHoldEnforcementResult, masterHoldTypes } from '@planning/utils/hold-enforcement'
import { useTranslate } from '@tolgee/react'
import {
  ConfirmationDialog,
  ContainerMobile,
  Header,
  StepperInfo,
  TomAutoComplete,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { escapeTruckNumber } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { NavigationStackCloseButton } from '../TallymanV2/Components/HeaderComponents/NavigationStackCloseButton'
import { useSelectedGateLabel } from './hooks/useSelectedGateLabel'
import { IInspectContainerFormData, InspectContainer } from './Components/InspectContainer'
import { TruckVisitDetails } from './Components/Molecules/TruckVisitDetails'
import { TruckVisitHeaderWithAction } from './Components/Molecules/TruckVisitHeaderWithAction'
import { OrderHandlingBox } from './Components/OrderHandlingBox'
import { NoOrdersHint } from './NoOrdersHint'
import { SearchDropOffOrders } from './SearchDropOffOrders'
import { SearchPickUpOrders } from './SearchPickUpOrders'

interface IProps {
  truckVisitWithOrders?: TruckVisitWithOrdersDto | null
  store: GateInViewStore
  onSubmit: (data: IGateInFormData) => Promise<void> | void
  validateAllocationSpace?: (inboundOrderIds: number[]) => Promise<number[]>
}

export interface IGateInFormData {
  truckVisitId?: number
  truckPlate: string
  trailerPlate?: string
  truckCompanyId?: number | null
  externalDriverId?: number | null
  driverName?: string
  driverID?: string
  truckCompanyName?: string
  truckCompanyAddress?: string
  eta: string | null
  etd: string | null
  dropOffOrders: IInspectContainerFormData[]
  pickUpOrders: IInspectContainerFormData[]
  bookings: IInspectContainerFormData[]
  generalCargoOrders: IInspectContainerFormData[]
  nnrOrders: NonNumericOrderWithPickUpAmount[]
  truckAppointmentDate?: string | null
  externalPortGatePassRef?: string | null
  // Clerk's session gate captured at gate-in start; stamped as the entry gate.
  entryGateId?: string | null
  // Optional exit gate the clerk designates at gate-in.
  designatedExitGateId?: string | null
}

export const GateInForm: FC<IProps> = observer(props => {
  const { store, truckVisitWithOrders, validateAllocationSpace, onSubmit } = props

  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    reset,
  } = useForm<IGateInFormData>({
    defaultValues: {
      truckPlate: store.licensePlate,
      trailerPlate: store.trailerPlate,
      truckCompanyName: truckVisitWithOrders?.truckCompany?.name ?? undefined,
      truckCompanyId: truckVisitWithOrders?.truckCompany?.id ?? undefined,
      driverName: truckVisitWithOrders?.driver?.name ?? undefined,
      driverID: truckVisitWithOrders?.driver?.driverID ?? undefined,
      dropOffOrders: store.dropOffOrders,
      pickUpOrders: store.pickUpOrders,
      generalCargoOrders: store.generalCargoOrders,
      nnrOrders: store.nnrOrders,
      bookings: [],
      // Re-seed from the store: this form unmounts on every sub-screen push
      // (NavigationStack renders only the top view), so RHF-only state dies with it.
      designatedExitGateId: store.designatedExitGateId,
    },
  })

  useEffect(() => {
    reset({
      truckPlate: store.licensePlate,
      dropOffOrders: store.dropOffOrders,
      pickUpOrders: store.pickUpOrders,
      generalCargoOrders: store.generalCargoOrders,
      nnrOrders: store.nnrOrders,
      bookings: [],
      // Preserve the clerk's designated-exit-gate choice across order-driven resets.
      designatedExitGateId: getValues('designatedExitGateId'),
    })
  }, [
    store.licensePlate,
    store.dropOffOrders,
    store.pickUpOrders,
    store.generalCargoOrders,
    store.nnrOrders,
    reset,
    getValues,
  ])
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const navigationStore = useNavigationStore()
  const { tenantStore, holdItemStore, gateSessionStore } = usePlanningStore()

  // Capture the bound session gate once, when the gate-in starts. Switching gates
  // mid-flow (Decision #5) must not re-stamp this in-flight visit — so this is read
  // at mount, not live at submit.
  const [entryGateId] = useState(() => gateSessionStore.boundGate?.id ?? null)

  // Designated-exit-gate picker lists only outbound-capable gates. Read the observable
  // in the render body (not inside the Controller closure) so SignalR updates track.
  const exitGateOptions = gateSessionStore.availableGates.filter(
    g => g.direction === GateDirection.Out || g.direction === GateDirection.Both,
  )

  store.parentStore.validateAllocationSpace = validateAllocationSpace

  const onSelectDropOff = (order: OrderResponseDto): void => {
    navigationStore.push(
      <InspectContainer
        order={order}
        submitButtonText={t('save', 'Save')}
        displayDamageReport
        onSubmit={data => {
          const handleSubmit = (orderData: IInspectContainerFormData) => {
            store.upsertDropOffOrder(orderData)
            navigationStore.pop()
            navigationStore.pop()
          }

          if (data.hasDamage) {
            handleReportDamage(data, handleSubmit)
          } else {
            handleSubmit(data)
          }
        }}
      />,
    )
  }

  const onAddDropOff = () => {
    store.parentStore.dropOffOrderSearchStore.setFilter('')
    navigationStore.push(
      <SearchDropOffOrders
        store={store.parentStore.dropOffOrderSearchStore}
        selectedOrders={store.dropOffOrders}
        onClickOrder={onSelectDropOff}
        onClickGCOrder={onSelectGeneralCargoOrder}
      />,
    )
  }

  const onSelectGeneralCargoOrder = (order: OrderResponseDto): void => {
    store.upsertDropOffOrder({
      ...order,
      carrierType: order.carrierVisitType ?? 'Universal',
      damages: [],
    })
    navigationStore.pop()
  }

  const onSelectPickUp = (order: OrderResponseDto): void => {
    // [GateClerk] TODO: do we need the damages here as well?
    store.upsertPickUpOrder({
      ...order,
      carrierType: order.carrierVisitType ?? 'Universal',
      damages: [] as ContainerDamageResponseDto[],
    })
    navigationStore.pop()
  }

  const onAddPickUp = () => {
    store.parentStore.pickUpOrderSearchStore.setFilter('')
    navigationStore.push(
      <SearchPickUpOrders
        store={store.parentStore.pickUpOrderSearchStore}
        selectedOrders={store.pickUpOrders}
        onClickOrder={onSelectPickUp}
        selectedNNROrders={store.nnrOrders}
        onClickNNROrder={nnrOrder => {
          store.upsertNNROrder(nnrOrder)
          navigationStore.pop()
        }}
        validateOutboundRequest={store.validateOutboundRequest}
      />,
    )
  }

  const onEditDropOffOrder = (order: IInspectContainerFormData) => {
    navigationStore.push(
      <InspectContainer
        order={order}
        damages={order.damagesReported}
        submitButtonText={t('save', 'Save')}
        displayDamageReport
        onSubmit={data => {
          // console.log('InspectContainer handleSubmit', data)

          const handleSubmit = (orderData: IInspectContainerFormData) => {
            store.upsertDropOffOrder(orderData)
            navigationStore.pop()
          }

          if (data.hasDamage) {
            handleReportDamage(data, handleSubmit)
          } else {
            handleSubmit(data)
          }
        }}
      />,
    )
  }

  const onEditPickUpOrder = (order: IInspectContainerFormData) => {
    navigationStore.push(
      <InspectContainer
        order={order}
        damages={order.damagesReported}
        submitButtonText={t('save', 'Save')}
        onSubmit={data => {
          store.upsertPickUpOrder(data)
          navigationStore.pop()
        }}
      />,
    )
  }

  const handleReportDamage = (
    orderData: IInspectContainerFormData,
    onConfirm: (orderData: IInspectContainerFormData) => void,
  ) => {
    navigationStore.push(
      <ContainerDamageRecordStep
        orderData={orderData}
        containerNumber={orderData?.containerNumber}
        onSubmit={(data, damages) => {
          data.damagesReported = damages
          navigationStore.pop()

          onConfirm(data)
        }}
      />,
    )
  }

  const showNoOrdersHint = !!errors.dropOffOrders && !!errors.pickUpOrders

  const validateOrderCanProceed = (f: IGateInFormData) => {
    const nnrOrders = f.nnrOrders ?? []
    const dropOffOrders = f.dropOffOrders ?? []
    const pickUpOrders = f.pickUpOrders ?? []
    const master = masterHoldTypes.get()

    return (
      nnrOrders.some(o => o.suitableContainersCount) ||
      f.generalCargoOrders?.length > 0 ||
      [...dropOffOrders, ...pickUpOrders].some(o => {
        const holds =
          o.journeyId != null
            ? (holdItemStore.holdsByContainerJourneyId[o.journeyId] ?? o.holds ?? [])
            : (o.holds ?? [])
        return canOrderProceed({
          allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
          direction: o.direction,
          hasBlockingHold: getHoldEnforcementResult(
            holds,
            o.direction === CarrierVisitDirection.Inbound ? 'GateIn' : 'GateOut',
            o.direction,
            master,
          ).isBlocking,
          isOnTerminal: o.isOnTerminal,
          skipReleaseOrder: tenantStore.skipReleaseOrder,
        })
      })
    )
  }

  const GateInAction: FC = observer(() => {
    return (
      <Button
        data-cy='gate-in-next-btn'
        variant='contained'
        type='submit'
        sx={{ width: '15%', minWidth: '100px' }}
        style={
          !validateOrderCanProceed(getValues()) || !store.licensePlate
            ? {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[500],
                boxShadow: '0 8px 16px 0 rgba(249, 250, 251, 0.24)',
              }
            : {}
        }
      >
        {t('next', 'Next')}
      </Button>
    )
  })

  const TruckAppointmentAction: FC = observer(() => {
    const allDropOffOrdersChecked = store.dropOffOrders.every(o => o.isChecked || !!o.commodityId)
    const isDisabled =
      !allDropOffOrdersChecked ||
      !validateOrderCanProceed(getValues()) ||
      truckVisitWithOrders?.driver?.banned

    return (
      <Button
        data-cy='gate-in-next-btn'
        variant='contained'
        color='primary'
        type='submit'
        sx={{ width: '15%', minWidth: '100px' }}
        disabled={isDisabled}
      >
        {t('gateIn', 'Gate in')}
      </Button>
    )
  })

  const Dialogs: FC = observer(() => {
    const onClose = () => {
      store.reset()
      navigationStore.pop()
    }

    const deleteOrder = () => {
      if (!store.selectedOrder) return

      const { id, direction } = store.selectedOrder

      if (direction === CarrierVisitDirection.Inbound) store.deleteDropOffOrder(id)
      else store.deletePickUpOrder(id)

      store.resetDialogs()
    }

    const deleteNNROrder = () => {
      if (!store.selectedNNROrder) return

      store.deleteNNROrder(store.selectedNNROrder.id)
      store.resetDialogs()
    }

    const onConfirmRemoval = () => (store.selectedOrder ? deleteOrder() : deleteNNROrder())

    return (
      <>
        <ConfirmationDialog
          open={store.isRemoveDialogOpen}
          title={`${t('sureYouWantToRemove', 'Sure you want to remove')} ${
            store.selectedOrder?.containerNumber ?? store.selectedNNROrder?.referenceNumber ?? ''
          }?`}
          primaryActionText={t('remove', 'Remove')}
          closeLabel={t('cancel', 'Cancel')}
          onConfirm={onConfirmRemoval}
          onClose={store.resetDialogs}
          mobile
        />

        <ConfirmationDialog
          open={store.isCancelGateDialogOpen}
          title={t('cancelGateIn?', 'Cancel gate in?')}
          message={t('anyChangesYouMadeWillBeLost', 'Any changes you made will be lost')}
          closeLabel={t('noDontCancel', `No, don't cancel`)}
          primaryActionText={t('cancel', 'Cancel')}
          onClose={store.resetDialogs}
          onConfirm={onClose}
          mobile
        />
      </>
    )
  })

  const HeaderWithBreadcrumbs: FC = observer(() => {
    const gateLabel = useSelectedGateLabel()

    return (
      <>
        <Header
          title={t('gateIn', 'Gate in')}
          subTitle={gateLabel}
          leftRenderOption={
            <NavigationStackCloseButton onClose={() => store.openCancelGateDialog()} />
          }
          rightRenderOption={<GateInAction />}
        />
        <StepperInfo
          title={t('recordVisitDetails', 'Record visit details')}
          steps={`1 ${t('of', 'of')} 2`}
        />
      </>
    )
  })

  const onTruckPlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = escapeTruckNumber(e.target.value)
    store.setLicensePlate(e.target.value)
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.grey[100] }}>
      <form onSubmit={handleSubmit(data => onSubmit({ ...data, entryGateId }))}>
        {tenantStore.skipTruckAppointment ? (
          <HeaderWithBreadcrumbs />
        ) : (
          <TruckVisitHeaderWithAction truckVisit={truckVisitWithOrders}>
            <TruckAppointmentAction />
          </TruckVisitHeaderWithAction>
        )}
        <Box sx={{ padding: '1rem' }}>
          <ContainerMobile>
            <Stack gap={2}>
              {truckVisitWithOrders && <TruckVisitDetails truckVisit={truckVisitWithOrders} />}
              <NoOrdersHint show={showNoOrdersHint} />

              {tenantStore.skipTruckAppointment && (
                <TextField
                  data-cy='gate-in-truck-plate-input'
                  fullWidth
                  label={`${t('truckPlateNumber', 'Truck plate number')}*`}
                  variant='outlined'
                  {...register('truckPlate', { required: true })}
                  name='truckPlate'
                  onChange={onTruckPlateChange}
                  sx={{ m: '1rem 0' }}
                  error={!!errors.truckPlate}
                  helperText={
                    errors.truckPlate
                      ? t('truckPlateNumberMandatory', 'Truck plate number is mandatory')
                      : ''
                  }
                />
              )}

              {exitGateOptions.length > 0 && (
                <Controller
                  control={control}
                  name='designatedExitGateId'
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <TomAutoComplete
                        sx={{ width: '100%' }}
                        options={exitGateOptions}
                        entityKey='id'
                        entityDisplayValue='name'
                        dataCy='designated-exit-gate-autocomplete'
                        label={t('designatedExitGate', 'Designated exit gate')}
                        multiple={false}
                        defaultValue={exitGateOptions.find(o => o.id === value)}
                        onChangeEntity={entity => {
                          const gateId = entity?.id ?? null
                          // Write through to the store so the choice survives the
                          // unmount/remount caused by sub-screen navigation.
                          store.setDesignatedExitGateId(gateId)
                          onChange(gateId)
                        }}
                        renderOption={(optionProps, option) => (
                          <Box
                            {...optionProps}
                            key={option.id}
                            component='li'
                            data-cy={`exit-gate-option-${option.id}`}
                          >
                            <GateDirectionIcon
                              direction={option.direction}
                              fontSize='small'
                              sx={{ mr: 1 }}
                            />
                            <Typography variant='body1'>
                              {option.code ? `${option.code} — ${option.name}` : option.name}
                            </Typography>
                          </Box>
                        )}
                      />
                    </FormControl>
                  )}
                />
              )}
              <Controller
                control={control}
                name={`dropOffOrders`}
                rules={{ validate: (_, formData) => validateOrderCanProceed(formData) }}
                render={() => {
                  return (
                    <OrderHandlingBox
                      orders={store.dropOffOrders.map(o => ({
                        ...o,
                        direction: CarrierVisitDirection.Inbound,
                      }))}
                      type='DropOff'
                      isContainerCheckRequired={!tenantStore.skipTruckAppointment}
                      onAddOrder={tenantStore.skipTruckAppointment ? onAddDropOff : undefined}
                      onRemoveOrder={
                        tenantStore.skipTruckAppointment ? store.openRemoveDialog : undefined
                      }
                      onEditOrder={onEditDropOffOrder}
                      error={showNoOrdersHint}
                      hideHoldEnforcement={true}
                    />
                  )
                }}
              />
              <Controller
                control={control}
                name={`pickUpOrders`}
                rules={{ validate: (_, formData) => validateOrderCanProceed(formData) }}
                render={() => {
                  return (
                    <OrderHandlingBox
                      orders={store.pickUpOrders.map(o => ({
                        ...o,
                        direction: CarrierVisitDirection.Outbound,
                      }))}
                      nnrOrders={store.nnrOrders}
                      type='PickUp'
                      onAddOrder={tenantStore.skipTruckAppointment ? onAddPickUp : undefined}
                      onRemoveOrder={
                        tenantStore.skipTruckAppointment ? store.openRemoveDialog : undefined
                      }
                      onEditOrder={tenantStore.skipTruckAppointment ? onEditPickUpOrder : undefined}
                      onRemoveNNROrder={
                        tenantStore.skipTruckAppointment ? store.openRemoveDialog : undefined
                      }
                      error={showNoOrdersHint}
                      validateOutboundRequest={store.validateOutboundRequest}
                      hideHoldEnforcement={true}
                    />
                  )
                }}
              />
            </Stack>

            <Dialogs />
          </ContainerMobile>
        </Box>
      </form>
    </Box>
  )
})
